/**
 * 👢 BOOTS
 *
 * Foot armor equipment items (15 total)
 * Level requirements: 5-90 (increments of 5)
 * Stat requirements: STR for heavy boots, DEX for agility boots
 *
 * Level Distribution:
 * - Common (3): Lv 5, 10, 15
 * - Uncommon (3): Lv 20, 25, 30
 * - Rare (3): Lv 35, 40, 45
 * - Epic (3): Lv 50, 55, 60
 * - Legendary (3): Lv 70, 80, 90
 */

export const BOOTS = {
  // ============================================================
  // COMMON BOOTS (3) - Lv 5, 10, 15
  // ============================================================
  leatherBoots: {
    id: 'leatherBoots',
    name: 'Leather Boots',
    icon: '👢',
    slot: 'boots',
    rarity: 'common',
    levelReq: 5,
    requirements: {},
    description: 'Simple boots for basic foot protection.',
    stats: {
      speed: 0,
      defense: 1,
    },
  },
  woodenSandals: {
    id: 'woodenSandals',
    name: 'Wooden Sandals',
    icon: '🩴',
    slot: 'boots',
    rarity: 'common',
    levelReq: 10,
    requirements: { DEX: 8 },
    description: 'Light and airy, but prone to splinters.',
    stats: {
      speed: 1,
      defense: 0,
    },
  },
  ironSabatons: {
    id: 'ironSabatons',
    name: 'Iron Sabatons',
    icon: '👢',
    slot: 'boots',
    rarity: 'common',
    levelReq: 15,
    requirements: { STR: 15 },
    description: 'Heavy iron boots, slow but protective.',
    stats: {
      speed: 0,
      defense: 2,
    },
  },

  // ============================================================
  // UNCOMMON BOOTS (3) - Lv 20, 25, 30
  // ============================================================
  steelBoots: {
    id: 'steelBoots',
    name: 'Steel Boots',
    icon: '👢',
    slot: 'boots',
    rarity: 'uncommon',
    levelReq: 20,
    requirements: { STR: 32 },
    description: 'Well-balanced steel footwear.',
    stats: {
      speed: 1,
      defense: 3,
    },
  },
  swiftRunners: {
    id: 'swiftRunners',
    name: 'Swift Runners',
    icon: '👟',
    slot: 'boots',
    rarity: 'uncommon',
    levelReq: 25,
    requirements: { DEX: 40 },
    description: 'Enchanted boots that increase your speed.',
    stats: {
      speed: 1,
      defense: 2,
    },
  },
  warBoots: {
    id: 'warBoots',
    name: 'War Boots',
    icon: '🥾',
    slot: 'boots',
    rarity: 'uncommon',
    levelReq: 30,
    requirements: { STR: 48 },
    description: 'Reinforced boots for the battlefield.',
    stats: {
      speed: 0,
      defense: 4,
    },
  },

  // ============================================================
  // RARE BOOTS (3) - Lv 35, 40, 45
  // ============================================================
  dragonrider: {
    id: 'dragonrider',
    name: 'Dragonrider Boots',
    icon: '🐉',
    slot: 'boots',
    rarity: 'rare',
    levelReq: 35,
    requirements: { STR: 50, DEX: 30 },
    description: 'Boots worn by legendary dragon riders.',
    stats: {
      speed: 2,
      defense: 4,
    },
  },
  shadowstep: {
    id: 'shadowstep',
    name: 'Shadowstep',
    icon: '🌑',
    slot: 'boots',
    rarity: 'rare',
    levelReq: 40,
    requirements: { DEX: 70 },
    description: 'Move silently through the shadows.',
    stats: {
      speed: 2,
      defense: 3,
    },
  },
  plateGreaves: {
    id: 'plateGreaves',
    name: 'Plate Greaves',
    icon: '👢',
    slot: 'boots',
    rarity: 'rare',
    levelReq: 45,
    requirements: { STR: 80 },
    description: 'Full plate protection for your legs.',
    stats: {
      speed: 1,
      defense: 6,
    },
  },

  // ============================================================
  // EPIC BOOTS (3) - Lv 50, 55, 60
  // ============================================================
  windwalker: {
    id: 'windwalker',
    name: 'Windwalker',
    icon: '💨',
    slot: 'boots',
    rarity: 'epic',
    levelReq: 50,
    requirements: { DEX: 100 },
    description: 'Walk on air itself with these mystical boots.',
    stats: {
      speed: 3,
      defense: 6,
    },
  },
  titanGreaves: {
    id: 'titanGreaves',
    name: 'Titan Greaves',
    icon: '⚡',
    slot: 'boots',
    rarity: 'epic',
    levelReq: 55,
    requirements: { STR: 115 },
    description: 'Each step shakes the earth.',
    stats: {
      speed: 2,
      defense: 9,
    },
  },
  voidwalkers: {
    id: 'voidwalkers',
    name: 'Voidwalkers',
    icon: '🌌',
    slot: 'boots',
    rarity: 'epic',
    levelReq: 60,
    requirements: { DEX: 90, INT: 50 },
    description: 'Step between dimensions with ease.',
    stats: {
      speed: 3,
      defense: 7,
    },
  },

  // ============================================================
  // LEGENDARY BOOTS (3) - Lv 70, 80, 90
  // ============================================================
  mercuryWings: {
    id: 'mercuryWings',
    name: 'Mercury Wings',
    icon: '👢',
    slot: 'boots',
    rarity: 'legendary',
    levelReq: 70,
    requirements: { DEX: 140 },
    description: 'Wings on your heels grant godlike speed.',
    stats: {
      speed: 4,
      defense: 10,
    },
  },
  infinityStride: {
    id: 'infinityStride',
    name: 'Infinity Stride',
    icon: '✨',
    slot: 'boots',
    rarity: 'legendary',
    levelReq: 80,
    requirements: { DEX: 120, INT: 60 },
    description: 'Walk across universes in a single step.',
    stats: {
      speed: 4,
      defense: 11,
    },
  },
  eternalsole: {
    id: 'eternalsole',
    name: 'Eternalsole',
    icon: '🌟',
    slot: 'boots',
    rarity: 'legendary',
    levelReq: 90,
    requirements: { STR: 150, DEX: 80 },
    description: 'These boots will never wear out.',
    stats: {
      speed: 3,
      defense: 13,
    },
  },
};
